/**
 * Centralized product data store for tracking
 * Caches product data to provide full details when tracking events
 */
import { getProductAnyId, type Product } from "@/lib/product-utils";

// Extend Window interface for debugging
declare global {
  interface Window {
    productStore?: ProductStore;
  }
}

class ProductStore {
  private products: Map<string, Product> = new Map();
  private storageKey = "product_cache";
  private maxCacheSize = 100; // Limit memory usage

  constructor() {
    // Load from localStorage on init if available
    if (typeof window !== "undefined") {
      this.loadFromStorage();
    }
  }

  /**
   * Register a product in the store
   * Called when products are loaded/displayed
   */
  registerProduct(product: Product): void {
    const rawKey = getProductAnyId(product);
    if (rawKey === "unknown") {
      return;
    }
    const productKey = rawKey.toLowerCase();

    // Add to memory cache
    this.products.set(productKey, product);

    // Limit cache size
    if (this.products.size > this.maxCacheSize) {
      const firstKey = this.products.keys().next().value;
      if (firstKey !== undefined) {
        this.products.delete(firstKey);
      }
    }

    // Persist to localStorage
    this.saveToStorage();
  }

  /**
   * Register multiple products at once
   */
  registerProducts(products: Product[]): void {
    products.forEach((product) => this.registerProduct(product));
  }

  /**
   * Get product data by UUID (case-insensitive)
   */
  getProduct(uuid: string): Product | null {
    return this.products.get(uuid.toLowerCase()) || null;
  }

  /**
   * Get tracking data for a product (case-insensitive UUID lookup)
   */
  getTrackingData(uuid: string): Record<string, unknown> {
    const product = this.getProduct(uuid);

    if (!product) {
      return {
        productUuid: uuid,
        productNotFound: true,
      };
    }

    // Centralized control of what product data to track
    // Easy to modify in one place
    return {
      productUuid: uuid,
      productName: product.name || "Unknown",
      productBrand: product.brand_name,
      productCatalog: product.catalog,
      productPrice: product.current_price,
      productOriginalPrice: product.original_price,
      productCurrency: "USD", // Default currency
      productRating: product.rating,
      productImageUrl: product.images?.[0],
      productUrl: product.url,
      // Add any other fields we want to track
    };
  }

  /**
   * Clear the store
   */
  clear(): void {
    this.products.clear();
    if (typeof window !== "undefined") {
      localStorage.removeItem(this.storageKey);
    }
  }

  /**
   * Load from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        if (Array.isArray(data)) {
          data.forEach((product) => {
            const rawKey = getProductAnyId(product);
            if (rawKey !== "unknown") {
              this.products.set(rawKey.toLowerCase(), product);
            }
          });
        }
      }
    } catch (error) {
      console.warn("Failed to load product cache from storage:", error);
    }
  }

  /**
   * Save to localStorage
   */
  private saveToStorage(): void {
    try {
      // Only save the most recent products to avoid localStorage limits
      const recentProducts = Array.from(this.products.values()).slice(-50);
      localStorage.setItem(this.storageKey, JSON.stringify(recentProducts));
    } catch (error) {
      console.warn("Failed to save product cache to storage:", error);
    }
  }
}

// Singleton instance
export const productStore = new ProductStore();

// Expose to window for debugging in development
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  window.productStore = productStore;
}

/** localStorage key prefix used by {@link cacheProductForPDP} and the PDP read side. */
const PDP_CACHE_KEY_PREFIX = "product-";

/**
 * Write a product into the short-lived localStorage cache so the PDP can
 * render it instantly on the next navigation.  The key is always lowercased
 * to match the read side in `getCachedProduct`.
 */
export function cacheProductForPDP(productId: string, product: Product): void {
  try {
    localStorage.setItem(
      `${PDP_CACHE_KEY_PREFIX}${productId.toLowerCase()}`,
      JSON.stringify(product)
    );
  } catch {
    // localStorage unavailable or quota exceeded – navigation still works.
  }
}

/**
 * Read and remove the short-lived PDP cache entry written by
 * {@link cacheProductForPDP}.  Returns `null` on miss.
 */
export function popCachedPDPProduct(productId: string): Product | null {
  const key = `${PDP_CACHE_KEY_PREFIX}${productId.toLowerCase()}`;
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw) as Product;
      window.setTimeout(() => {
        try {
          localStorage.removeItem(key);
        } catch {
          // ignore
        }
      }, 30_000);
      return parsed;
    }
  } catch {
    // storage unavailable or bad JSON
  }
  return null;
}

/**
 * Hook to register products when they're rendered
 * This ensures product data is available for tracking
 */
export function useRegisterProducts(products: Product | Product[] | null | undefined): void {
  if (!products) return;

  const productsArray = Array.isArray(products) ? products : [products];
  productStore.registerProducts(productsArray);
}
